import { useState, useEffect } from 'react';
import { TextInput } from 'carbon-components-react';
import CustomButton from '../../components/custom-button/custom-btn.component';
import './SBAForm.scss';
import {
  getItemById,
  useReducerSpread,
  padArray,
  getQueryString,
} from '../../utils/common.util';
import useSWR from 'swr';
import useSWRMutation from 'swr/mutation';
import { putMethod } from '../../api/common';
import { useSelector } from 'react-redux';
import { selectCurrentUser } from '../../redux/selectors/user.selectors';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import {
  calculateTotal,
  calculateSBA,
  calculateTotalMarks,
  calculateFinalPercentage,
  SBAAbbreviationsNote,
} from './calculation-helpers';
import { useQueryParams } from '../../utils/useQueryParams';
import { StatsBar } from '../../components/PrintOutHead';
import PageLayout from '../../components/PageLayout';

const max = {
  classExercises: 10,
  homeworks: 10,
  cats: 10,
  project: 20,
};

export default function EditSBA() {
  const history = useHistory();
  const {
    subjectId: urlSubjectId,
    classId: urlClassId,
    termId: urlTermId,
    academicYearId: urlAcademicYearId,
  } = useQueryParams('subjectId', 'classId', 'termId', 'academicYearId');

  if (!urlSubjectId || !urlClassId || !urlTermId || !urlAcademicYearId) {
    history.push('/sba-view');
  }

  const [formData] = useReducerSpread({
    subjectId: urlSubjectId ?? '',
    classId: urlClassId ?? '',
    termId: urlTermId ?? '',
    academicYearId: urlAcademicYearId ?? '',
  });

  const { data: academicYears } = useSWR(
    'academic-year/list/?skip=0&take=100&query=',
  );

  const { data: classes } = useSWR('class/list');
  const { data: subjects } = useSWR(
    formData.classId
      ? `academic/subject/list/?classId=${formData.classId}`
      : null,
  );

  const {
    data: sbaData,
    isLoading: isSbaLoading,
    error: fetchError,
  } = useSWR(
    formData.classId && formData.subjectId
      ? `academic/grading/list-school-based-assessment?${getQueryString(formData)}`
      : null,
  );

  const [scores, setScores] = useState({});

  useEffect(() => {
    if (sbaData) {
      const initialScores = sbaData.reduce((acc, record) => {
        const studentKey = `student_${record.student.id}`;
        acc[studentKey] = {
          classExercises: padArray(record.classExercises, 10, ''),
          homeworks: padArray(record.homeworks, 10, ''),
          cats: padArray(record.classTests, 3, ''),
          project: record.projectWork ?? '',
        };
        return acc;
      }, {});
      setScores(initialScores);
    }
  }, [sbaData]);

  const handleScoreChange = (studentId, type, idx, value) => {
    if (value === '' || (Number(value) >= 0 && Number(value) <= max[type])) {
      setScores((prev) => {
        const studentKey = `student_${studentId}`;
        const prevStudent = prev[studentKey] || {
          classExercises: Array(10).fill(''),
          homeworks: Array(10).fill(''),
          cats: Array(3).fill(''),
          project: '',
        };
        let updated = { ...prevStudent };
        if (type === 'project') {
          updated.project = value;
        } else {
          const arr = [...updated[type]];
          arr[idx] = value;
          updated[type] = arr;
        }
        return { ...prev, [studentKey]: updated };
      });
    }
  };

  const currentUser = useSelector(selectCurrentUser);
  const { trigger, error, isMutating } = useSWRMutation(
    `academic/grading/update-school-based-assessment`,
    (url, { arg }) => putMethod(url, arg, currentUser.authToken),
    {
      onSuccess: () => {
        history.push(`/sba-view?${getQueryString(formData)}`);
      },
      throwOnError: false,
    },
  );

  const handleSubmit = (e) => {
    e.preventDefault();

    const sbaRecords = sbaData.map((record) => {
      const studentKey = `student_${record.student.id}`;
      const studentData = scores[studentKey] || {}; // Use empty object as default

      return {
        id: record.id,
        classExercises: (studentData.classExercises || [])
          .filter((s) => s !== '' && s !== null)
          .map(Number),
        homeworks: (studentData.homeworks || [])
          .filter((s) => s !== '' && s !== null)
          .map(Number),
        classTests: (studentData.cats || [])
          .filter((s) => s !== '' && s !== null)
          .map(Number),
        projectWork: Number(studentData.project || 0),
      };
    });

    const payload = {
      subjectId: Number(formData.subjectId),
      sbaRecords,
    };

    trigger(payload);
  };

  return (
    <PageLayout
      breadcrumbs={[
        { label: 'Academics', to: '/academic' },
        {
          label: 'View SBA',
          to: `/sba-view?${getQueryString(formData)}`,
        },
        { label: `Edit SBA`, active: true },
      ]}
      title="Edit SBA"
      isLoading={isSbaLoading}
    >
      <StatsBar
        statsItems={[
          {
            label: 'Class',
            value: getItemById(classes, formData.classId)?.description,
          },
          {
            label: 'Subject',
            value: getItemById(subjects, formData.subjectId)?.description,
          },

          {
            label: 'Academic Year',
            value: getItemById(academicYears, formData.academicYearId)
              ?.description,
          },
          {
            label: 'Term',
            value: sbaData?.[0]?.term?.description,
          },
        ]}
      />
      {/* display error message */}
      {error && <p className="text-14 mt-20 text-danger">{error.message}</p>}
      {fetchError && (
        <p className="text-14 mt-10 text-danger">{fetchError.message}</p>
      )}

      <form
        className="mt-10 sba-form"
        onSubmit={handleSubmit}
        autoComplete="off"
      >
        <div style={{ overflowX: 'auto' }}>
          {sbaData?.length > 0 && (
            <table className="bx--data-table sba-form__table sba-form__table--sticky-student">
              <thead>
                <tr>
                  <th rowSpan="2" className="sba-form__sticky-col">
                    Student
                  </th>
                  <th colSpan="10">CLASS EXERCISE</th>
                  <th rowSpan="2">
                    Total C.E. <br /> (10)
                  </th>
                  <th colSpan="10">HOMEWORK</th>
                  <th rowSpan="2">
                    Total H/W <br /> (10)
                  </th>
                  <th rowSpan="2">
                    Total <br /> (C.E. + H/W) <br /> (10)
                  </th>
                  <th colSpan="3">CATS</th>
                  <th rowSpan="2">
                    Project <br /> (20)
                  </th>
                  <th rowSpan="2">Total (60)</th>
                  <th rowSpan="2">Final % (50)</th>
                </tr>
                <tr>
                  {[...Array(10)].map((_, i) => (
                    <th key={`ce-head-${i}`}> {i + 1}</th>
                  ))}
                  {[...Array(10)].map((_, i) => (
                    <th key={`hw-head-${i}`}> {i + 1}</th>
                  ))}
                  {[...Array(3)].map((_, i) => (
                    <th key={`cat-head-${i}`}> {i + 1}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sbaData?.map((record) => {
                  const { student } = record;
                  const studentKey = `student_${student.id}`;
                  const s = scores[studentKey] || {
                    classExercises: Array(10).fill(''),
                    homeworks: Array(10).fill(''),
                    cats: Array(3).fill(''),
                    project: '',
                  };
                  const ce10 = calculateTotal(s.classExercises, 10);
                  const hw10 = calculateTotal(s.homeworks, 10);
                  const sba10 = calculateSBA(ce10, hw10);
                  const total60 = calculateTotalMarks(sba10, s.cats, s.project);
                  const final50 = calculateFinalPercentage(total60);
                  return (
                    <tr key={student.id}>
                      <td className="sba-form__student-name sba-form__sticky-col">
                        {student.fullName}
                      </td>
                      {s.classExercises.map((v, i) => (
                        <td key={i}>
                          <TextInput
                            className="sba-form__input"
                            type="number"
                            min="0"
                            max="10"
                            value={v}
                            onChange={(e) =>
                              handleScoreChange(
                                student.id,
                                'classExercises',
                                i,
                                e.target.value,
                              )
                            }
                            hideLabel
                            id={`ce-${student.id}-${i}`}
                            labelText=""
                            autoComplete="off"
                          />
                        </td>
                      ))}
                      <td>
                        <span className="sba-form__readonly">{ce10}</span>
                      </td>
                      {s.homeworks.map((v, i) => (
                        <td key={i}>
                          <TextInput
                            className="sba-form__input"
                            type="number"
                            min="0"
                            max="10"
                            value={v}
                            onChange={(e) =>
                              handleScoreChange(
                                student.id,
                                'homeworks',
                                i,
                                e.target.value,
                              )
                            }
                            hideLabel
                            id={`hw-${student.id}-${i}`}
                            labelText=""
                            autoComplete="off"
                          />
                        </td>
                      ))}
                      <td>
                        <span className="sba-form__readonly">{hw10}</span>
                      </td>
                      <td>
                        <span className="sba-form__readonly">{sba10}</span>
                      </td>
                      {s.cats.map((v, i) => (
                        <td key={i}>
                          <TextInput
                            className="sba-form__input"
                            type="number"
                            min="0"
                            max="10"
                            value={v}
                            onChange={(e) =>
                              handleScoreChange(
                                student.id,
                                'cats',
                                i,
                                e.target.value,
                              )
                            }
                            hideLabel
                            id={`cat-${student.id}-${i}`}
                            labelText=""
                            autoComplete="off"
                          />
                        </td>
                      ))}
                      <td>
                        <TextInput
                          className="sba-form__input"
                          type="number"
                          min="0"
                          max="20"
                          value={s.project}
                          onChange={(e) =>
                            handleScoreChange(
                              student.id,
                              'project',
                              0,
                              e.target.value,
                            )
                          }
                          hideLabel
                          id={`project-${student.id}`}
                          labelText=""
                          autoComplete="off"
                        />
                      </td>
                      <td>
                        <span className="sba-form__readonly">{total60}</span>
                      </td>
                      <td>
                        <span className="sba-form__readonly">{final50}</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}

          {sbaData?.length === 0 && !isSbaLoading && (
            <p className="text-14 mt-30 text-grey">
              No scores for this class and subject.
            </p>
          )}
        </div>

        {sbaData?.length > 0 && (
          <div
            style={{
              marginTop: 24,
              display: 'flex',
              justifyContent: 'space-between',
              gap: '1rem',
              flexWrap: 'wrap',
              alignItems: 'start',
            }}
          >
            <SBAAbbreviationsNote />

            <CustomButton
              type="submit"
              classes="primary font-bold"
              style={{ minWidth: 160 }}
              isLoading={isMutating}
            >
              UPDATE
            </CustomButton>
          </div>
        )}
      </form>
    </PageLayout>
  );
}
